import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  ArrowLeft,
  Plus,
  Minus,
  X,
  Send,
  Download,
  CalendarIcon,
  Users,
  FileText,
  Eye,
  ChefHat,
  Wine,
  Music,
  Truck,
  Image as ImageIcon,
  Phone,
  Mail,
  CheckCircle2,
  Loader2,
  MapPin,
  Clock,
  Building2,
  DollarSign,
  Percent,
  Upload,
  Sparkles,
  Save,
  CheckCircle,
  PlusCircle,
  Trash2,
  ShoppingCart,
  Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  food: <ChefHat className="h-4 w-4" />,
  drinks: <Wine className="h-4 w-4" />,
  entertainment: <Music className="h-4 w-4" />,
  staffing: <Users className="h-4 w-4" />,
  logistics: <Truck className="h-4 w-4" />
};

const EMAIL_TEMPLATES = [
  {
    id: 'initial',
    name: 'Initial Proposal',
    subject: 'Your Event Proposal from Eventism',
    body: `Hi {client_name},\n\nThank you for your interest in Eventism! We've prepared a custom proposal for your upcoming event.\n\nPlease click the link below to review the details, services, and pricing we've put together for you.\n\nWe look forward to making your event unforgettable!\n\nBest regards,\nEventism Team`
  },
  {
    id: 'followup',
    name: 'Follow-Up',
    subject: 'Following Up on Your Event Proposal - Eventism',
    body: `Hi {client_name},\n\nI wanted to follow up on the proposal we sent over. Have you had a chance to review it?\n\nWe'd love to answer any questions and discuss how we can customize the services to fit your needs perfectly.\n\nFeel free to reply to this email or call us anytime.\n\nBest regards,\nEventism Team`
  },
  {
    id: 'reminder',
    name: 'Expiration Reminder',
    subject: 'Your Eventism Proposal Expires Soon',
    body: `Hi {client_name},\n\nJust a friendly reminder that your event proposal from Eventism will expire soon.\n\nTo secure your event date and lock in the current pricing, please review and accept the proposal at your earliest convenience.\n\nDon't hesitate to reach out if you need any adjustments.\n\nBest regards,\nEventism Team`
  },
  {
    id: 'custom',
    name: 'Custom Message',
    subject: '',
    body: ''
  }
];

const EVENT_TYPES = [
  'Corporate Event',
  'Wedding',
  'Birthday Party',
  'Anniversary',
  'Holiday Party',
  'Graduation',
  'Baby Shower',
  'Bridal Shower',
  'Networking Event',
  'Product Launch',
  'Conference',
  'Gala',
  'Fundraiser',
  'Other'
];

const DEFAULT_TERMS = `1. This proposal is valid for 30 days from the date of issue.
2. A 50% deposit is required to confirm the booking.
3. Final payment is due 7 days before the event.
4. Cancellations made within 14 days of the event may be subject to cancellation fees.
5. Prices are subject to applicable taxes and service fees.
6. Final guest count must be confirmed 72 hours prior to the event.`;

// New elegant default header - abstract gold/dark design
const DEFAULT_HEADER_IMAGE = 'https://images.unsplash.com/photo-1753234506830-069cff1f25ae?w=1200&h=300&fit=crop';

// Header image gallery for selection
const HEADER_IMAGE_GALLERY = [
  { url: 'https://images.unsplash.com/photo-1753234506830-069cff1f25ae?w=1200&h=300&fit=crop', name: 'Gold Waves' },
  { url: 'https://images.unsplash.com/photo-1624624666976-45e074aa2574?w=1200&h=300&fit=crop', name: 'Diamond Pattern' },
  { url: 'https://images.unsplash.com/photo-1739475981160-48324d5f7f79?w=1200&h=300&fit=crop', name: 'Floral Emboss' },
  { url: 'https://images.pexels.com/photos/36327398/pexels-photo-36327398.jpeg?auto=compress&w=1200&h=300&fit=crop', name: 'Black Marble' },
  { url: 'https://images.pexels.com/photos/7405670/pexels-photo-7405670.jpeg?auto=compress&w=1200&h=300&fit=crop', name: 'Gold Texture' },
  { url: 'https://images.unsplash.com/photo-1540177656454-3f6c4547bed1?w=1200&h=300&fit=crop', name: 'White Marble' },
];

// Helper to format time to standard AM/PM format
const formatTimeToStandard = (time24) => {
  if (!time24) return 'TBD';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const standardHour = hour % 12 || 12;
  return `${standardHour}:${minutes} ${ampm}`;
};


// ============= PROPOSAL ACTIVITY & NOTES PANEL =============
function ProposalActivityPanel({ proposalId, token }) {
  const [data, setData] = useState({ activities: [], notes: [] });
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('activity');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/proposals/${proposalId}/activity`, { headers });
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (proposalId && token) loadData(); }, [proposalId, token]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/proposals/${proposalId}/notes`, { content: newNote }, { headers });
      setNewNote('');
      loadData();
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
    finally { setAdding(false); }
  };

  const ICONS = {
    email_sent: { icon: Mail, color: 'text-blue-400', bg: 'bg-blue-500/20' },
    client_viewed: { icon: Eye, color: 'text-amber-400', bg: 'bg-amber-500/20' },
    note_added: { icon: FileText, color: 'text-indigo-400', bg: 'bg-indigo-500/20' },
    status_changed: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
    created: { icon: Plus, color: 'text-slate-400', bg: 'bg-slate-500/20' },
  };

  return (
    <Card className="bg-card border-border" data-testid="activity-panel">
      <CardHeader className="pb-2">
        <div className="flex gap-2">
          <Button size="sm" variant={activeSubTab === 'activity' ? 'default' : 'ghost'} className="h-7 text-xs" onClick={() => setActiveSubTab('activity')} data-testid="activity-tab-btn">
            Activity
          </Button>
          <Button size="sm" variant={activeSubTab === 'notes' ? 'default' : 'ghost'} className="h-7 text-xs" onClick={() => setActiveSubTab('notes')} data-testid="notes-tab-btn">
            Notes ({data.notes?.length || 0})
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>
        ) : activeSubTab === 'activity' ? (
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="activity-list">
            {data.activities?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
            ) : data.activities.map((a, i) => {
              const iconInfo = ICONS[a.event_type] || ICONS.created;
              const IconComp = iconInfo.icon;
              return (
                <div key={i} className="flex items-start gap-2.5 text-xs" data-testid={`activity-${i}`}>
                  <div className={`p-1 rounded ${iconInfo.bg} mt-0.5`}>
                    <IconComp className={`h-3 w-3 ${iconInfo.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground">{a.description}</p>
                    <p className="text-[10px] text-muted-foreground">{new Date(a.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3" data-testid="notes-list">
            <div className="flex gap-2">
              <Input
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add private note..."
                className="bg-muted border-border h-8 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && addNote()}
                data-testid="note-input"
              />
              <Button size="sm" className="h-8" onClick={addNote} disabled={adding} data-testid="add-note-btn">Add</Button>
            </div>
            <div className="max-h-52 overflow-y-auto space-y-2">
              {data.notes?.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">No notes — add private notes here (not seen by client)</p>
              ) : data.notes.map((n, i) => (
                <div key={i} className="p-2 rounded bg-muted/50 text-xs" data-testid={`note-${i}`}>
                  <p className="text-foreground">{n.content}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{n.author} — {new Date(n.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function ProposalGenerator() {
  const { leadId, proposalId } = useParams();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') === 'invoice' ? 'invoice' : 'proposal';
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [lead, setLead] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [cityServices, setCityServices] = useState([]);
  const [selectedCity, setSelectedCity] = useState('all');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [showCustomServiceDialog, setShowCustomServiceDialog] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [customGalleryImages, setCustomGalleryImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [customizingItem, setCustomizingItem] = useState(null);
  const [customizationOptions, setCustomizationOptions] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [savedProposalId, setSavedProposalId] = useState(proposalId || null);
  const [proposalStatus, setProposalStatus] = useState('draft');
  const [proposalSlug, setProposalSlug] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({
    recipients: [],
    newRecipient: '',
    subject: '',
    body: '',
    templateId: 'initial'
  });
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');
  
  // Custom service state
  const [customService, setCustomService] = useState({
    name: '',
    description: '',
    category: 'custom',
    price: 0,
    quantity: 1,
    unit: 'service',
    what_to_expect: '',
    whats_included: ''
  });
  
  // Proposal state with all new fields
  const [proposal, setProposal] = useState({
    title: '',
    document_type: initialType, // 'proposal' or 'invoice'
    // Client Details
    client_name: '',
    client_email: '',
    client_phone: '',
    company_name: '',
    // Event Details
    event_name: '',
    event_type: '',
    event_date: null,
    event_time: '18:00',
    guest_count: 50,
    venue: '',
    venue_address: '',
    // Delivery/Pickup (for equipment rentals)
    delivery_date: null,
    delivery_time: '',
    pickup_date: null,
    pickup_time: '',
    // Content
    basic_details: '', // Intro text
    selected_items: [],
    notes: '',
    terms_conditions: DEFAULT_TERMS,
    // Branding
    header_image: DEFAULT_HEADER_IMAGE,
    use_default_header: true,
    // Pricing
    discount_percent: 0,
    admin_fee_percent: 18,
    delivery_fee: 0,
    tax_percent: 8,
    // Meta
    valid_until: null,
    status: 'draft',
    // Invoice-specific
    payment_terms: 'due_on_receipt',
    due_date: null,
    payment_status: 'unpaid',
    amount_paid: 0,
  });

  const isInvoice = proposal.document_type === 'invoice';
  const docLabel = isInvoice ? 'Invoice' : 'Proposal';

  // Admin settings for default branding
  const [adminSettings, setAdminSettings] = useState({
    default_header_image: DEFAULT_HEADER_IMAGE,
    company_phone: '(555) 123-4567',
    company_email: 'hello@eventism.com',
    company_address: '123 Event Street, Denver, CO 80202'
  });

  useEffect(() => {
    fetchData();
    fetchAdminSettings();
  }, [leadId, proposalId]);

  // Auto-save form to localStorage (only for new proposals/invoices, not editing)
  const DRAFT_KEY = `eventism_draft_${proposalId || 'new'}`;
  
  useEffect(() => {
    if (!proposalId && !isEditing) {
      // Restore draft from localStorage
      try {
        const saved = localStorage.getItem(DRAFT_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Restore dates as Date objects
          if (parsed.event_date) parsed.event_date = new Date(parsed.event_date);
          if (parsed.valid_until) parsed.valid_until = new Date(parsed.valid_until);
          if (parsed.delivery_date) parsed.delivery_date = new Date(parsed.delivery_date);
          if (parsed.pickup_date) parsed.pickup_date = new Date(parsed.pickup_date);
          if (parsed.due_date) parsed.due_date = new Date(parsed.due_date);
          setProposal(prev => ({ ...prev, ...parsed }));
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (!proposalId && !isEditing && (proposal.client_name || proposal.client_email || proposal.selected_items.length > 0)) {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(proposal));
      } catch {}
    }
  }, [proposal, proposalId, isEditing]);

  // Clear draft after successful save
  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
  };

  const fetchData = async () => {
    try {
      const [catalogRes, cityRes] = await Promise.all([
        axios.get(`${API}/services/catalog`),
        axios.get(`${API}/services/products?limit=200`).catch(() => ({ data: { products: [] } })),
      ]);
      setCatalog(catalogRes.data);
      setCityServices(cityRes.data?.products || cityRes.data || []);

      // If editing an existing proposal
      if (proposalId) {
        setIsEditing(true);
        const proposalRes = await axios.get(`${API}/proposals/${proposalId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const existingProposal = proposalRes.data;
        setProposal({
          ...existingProposal,
          event_date: existingProposal.event_date ? new Date(existingProposal.event_date) : null,
          valid_until: existingProposal.valid_until ? new Date(existingProposal.valid_until) : null,
          delivery_date: existingProposal.delivery_date ? new Date(existingProposal.delivery_date) : null,
          pickup_date: existingProposal.pickup_date ? new Date(existingProposal.pickup_date) : null,
          due_date: existingProposal.due_date ? new Date(existingProposal.due_date) : null,
          use_default_header: !existingProposal.header_image || existingProposal.header_image === DEFAULT_HEADER_IMAGE,
          terms_conditions: existingProposal.terms_conditions || DEFAULT_TERMS,
          document_type: existingProposal.document_type || 'proposal',
          payment_terms: existingProposal.payment_terms || 'due_on_receipt',
          payment_status: existingProposal.payment_status || 'unpaid',
          amount_paid: existingProposal.amount_paid || 0,
        });
        setProposalStatus(existingProposal.status || 'draft');
        setProposalSlug(existingProposal.slug || existingProposal.custom_slug || null);
        setSavedProposalId(proposalId);
      } else if (leadId) {
        const leadsRes = await axios.get(`${API}/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const foundLead = leadsRes.data.find(l => l.lead_id === leadId);
        if (foundLead) {
          setLead(foundLead);
          setProposal(prev => ({
            ...prev,
            title: `Proposal for ${foundLead.client_name}`,
            client_name: foundLead.client_name,
            client_email: foundLead.client_email,
            client_phone: foundLead.phone || '',
            company_name: foundLead.company_name || '',
            event_name: foundLead.event_name || `${foundLead.client_name}'s Event`,
            event_type: foundLead.event_type || '',
            event_date: foundLead.event_date ? new Date(foundLead.event_date) : null,
            guest_count: foundLead.guest_count || 50,
            venue: foundLead.venue || ''
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      
      // Check for marketplace services (from "Add to Proposal" flow)
      const marketplaceServices = sessionStorage.getItem('marketplace_services');
      if (marketplaceServices) {
        try {
          const items = JSON.parse(marketplaceServices);
          if (Array.isArray(items) && items.length > 0) {
            setProposal(prev => ({
              ...prev,
              selected_items: [
                ...prev.selected_items,
                ...items.map(svc => ({
                  item_id: svc.item_id || svc.service_id,
                  name: svc.name,
                  category: svc.service_category || svc.category,
                  description: svc.description,
                  price: svc.price || svc.base_price || 0,
                  pricing_model: svc.pricing_model || 'per_person',
                  option_groups: svc.option_groups || [],
                  quantity: ['per_person', 'per_hour_per_person'].includes(svc.pricing_model) ? 50 : 1,
                  image_url: svc.image_url,
                  what_to_expect: svc.what_to_expect,
                  whats_included: svc.whats_included,
                  service_details: '',
                  selected_options: {},
                  options_price: 0
                }))
              ]
            }));
            toast.success(`${items.length} service(s) loaded from marketplace`);
          }
        } catch (e) { /* ignore */ }
        sessionStorage.removeItem('marketplace_services');
      }
    }
  };

  const fetchAdminSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setAdminSettings(prev => ({
          ...prev,
          ...response.data
        }));
        if (response.data.default_proposal_header) {
          setProposal(prev => ({
            ...prev,
            header_image: response.data.default_proposal_header
          }));
        }
      }
    } catch (error) {
      // Settings may not exist yet, use defaults
    }
  };

  // Add item with initial quantity from guest count
  const addItem = (item) => {
    // If item has option_groups, open customization dialog first
    if (item.option_groups && item.option_groups.length > 0) {
      setCustomizingItem(item);
      setCustomizationOptions({});
      return;
    }
    addItemDirect(item, {});
  };

  const addItemDirect = (item, selectedOpts) => {
    const existing = proposal.selected_items.find(i => i.item_id === item.item_id);
    const isFoodOrDrinks = ['food', 'drinks'].includes(item.category?.toLowerCase());
    const isPP = item.pricing_model === 'per_person' || item.pricing_model === 'per_hour_per_person';
    const initialQuantity = item._localGuests || (isPP ? proposal.guest_count : (isFoodOrDrinks ? proposal.guest_count : 1));
    
    // Calculate options price impact
    let optionsPrice = 0;
    if (item.option_groups) {
      item.option_groups.forEach(og => {
        const selected = selectedOpts[og.option_id] || [];
        (Array.isArray(selected) ? selected : [selected]).forEach(choiceId => {
          const choice = (og.choices || []).find(c => c.choice_id === choiceId);
          if (choice) optionsPrice += parseFloat(choice.price_modifier) || 0;
        });
      });
    }
    
    // Clean temp fields
    const { _localGuests, _localHours, ...cleanItem } = item;
    
    if (existing) {
      setProposal(prev => ({
        ...prev,
        selected_items: prev.selected_items.map(i =>
          i.item_id === item.item_id ? { ...i, selected_options: selectedOpts, options_price: optionsPrice, quantity: _localGuests || i.quantity } : i
        )
      }));
      toast.success(`${item.name} updated`);
    } else {
      setProposal(prev => ({
        ...prev,
        selected_items: [...prev.selected_items, { 
          ...cleanItem, 
          quantity: initialQuantity,
          service_details: '',
          selected_options: selectedOpts,
          options_price: optionsPrice
        }]
      }));
      toast.success(`${item.name} added to proposal`);
    }
    setCustomizingItem(null);
    setCustomizationOptions({});
    setShowServiceDialog(false);
  };

  const openCustomization = (item) => {
    // Open customization for an item already in cart
    setCustomizingItem(item);
    setCustomizationOptions(item.selected_options || {});
  };

  const toggleOption = (optionId, choiceId, multiSelect) => {
    setCustomizationOptions(prev => {
      const current = prev[optionId] || (multiSelect ? [] : null);
      if (multiSelect) {
        const arr = Array.isArray(current) ? current : [];
        return { ...prev, [optionId]: arr.includes(choiceId) ? arr.filter(id => id !== choiceId) : [...arr, choiceId] };
      }
      return { ...prev, [optionId]: current === choiceId ? null : choiceId };
    });
  };

  const updateQuantity = (itemId, delta) => {
    setProposal(prev => ({
      ...prev,
      selected_items: prev.selected_items.map(i => {
        if (i.item_id === itemId) {
          const newQty = Math.max(1, i.quantity + delta);
          return { ...i, quantity: newQty };
        }
        return i;
      })
    }));
  };

  const updateItemDetails = (itemId, details) => {
    setProposal(prev => ({
      ...prev,
      selected_items: prev.selected_items.map(i => 
        i.item_id === itemId ? { ...i, service_details: details } : i
      )
    }));
  };

  const removeItem = (itemId) => {
    setProposal(prev => ({
      ...prev,
      selected_items: prev.selected_items.filter(i => i.item_id !== itemId)
    }));
  };

  const addCustomService = () => {
    if (!customService.name || customService.price <= 0) {
      toast.error('Please enter a service name and price');
      return;
    }
    
    const newItem = {
      item_id: `custom_${Date.now()}`,
      name: customService.name,
      description: customService.description,
      category: customService.category || 'custom',
      price: parseFloat(customService.price),
      quantity: parseInt(customService.quantity) || 1,
      unit: customService.unit || 'service',
      what_to_expect: customService.what_to_expect,
      whats_included: customService.whats_included ? customService.whats_included.split(',').map(s => s.trim()).filter(Boolean) : [],
      is_custom: true
    };
    
    setProposal(prev => ({
      ...prev,
      selected_items: [...prev.selected_items, newItem]
    }));
    
    // Reset custom service form
    setCustomService({
      name: '',
      description: '',
      category: 'custom',
      price: 0,
      quantity: 1,
      unit: 'service',
      what_to_expect: '',
      whats_included: ''
    });
    
    setShowCustomServiceDialog(false);
    toast.success('Custom service added!');
  };

  // Pricing calculations
  const calculateSubtotal = () => {
    return proposal.selected_items.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1)), 0);
  };

  const calculateDiscount = () => {
    return calculateSubtotal() * (proposal.discount_percent / 100);
  };

  const calculateAdminFee = () => {
    return (calculateSubtotal() - calculateDiscount()) * (proposal.admin_fee_percent / 100);
  };

  const calculateDeliveryFee = () => {
    return proposal.delivery_fee || 0;
  };

  const calculateTaxableAmount = () => {
    return calculateSubtotal() - calculateDiscount() + calculateAdminFee() + calculateDeliveryFee();
  };

  const calculateTax = () => {
    return calculateTaxableAmount() * (proposal.tax_percent / 100);
  };

  const calculateTotal = () => {
    return calculateTaxableAmount() + calculateTax();
  };

  const groupedItems = proposal.selected_items.reduce((acc, item) => {
    const cat = item.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const handleSendProposal = async () => {
    if (!proposal.client_email || proposal.selected_items.length === 0) {
      toast.error('Please add client email and at least one service');
      return;
    }

    setSending(true);
    try {
      const proposalData = {
        ...proposal,
        event_date: proposal.event_date?.toISOString(),
        valid_until: proposal.valid_until?.toISOString(),
        delivery_date: proposal.delivery_date?.toISOString(),
        pickup_date: proposal.pickup_date?.toISOString(),
        due_date: proposal.due_date?.toISOString(),
        lead_id: leadId || proposal.lead_id,
        subtotal: calculateSubtotal(),
        discount: calculateDiscount(),
        admin_fee: calculateAdminFee(),
        delivery_fee: calculateDeliveryFee(),
        tax: calculateTax(),
        total: calculateTotal()
      };

      const docLabel = proposal.document_type === 'invoice' ? 'Invoice' : 'Proposal';
      let resultId = savedProposalId;

      if (isEditing && savedProposalId) {
        await axios.patch(`${API}/proposals/${savedProposalId}`, proposalData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${docLabel} updated!`);
        clearDraft();
      } else {
        const res = await axios.post(`${API}/proposals`, proposalData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        resultId = res.data.proposal_id;
        setSavedProposalId(resultId);
        setIsEditing(true);
        setProposalStatus('draft');
        if (res.data.slug) setProposalSlug(res.data.slug);
        toast.success(`${docLabel} created!`);
        clearDraft();
        
        if (leadId) {
          await axios.patch(`${API}/leads/${leadId}`, {
            status: 'proposal_sent'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }
      
      // Stay on page in preview mode
      setPreviewMode(true);
      
      // Update browser URL to include the proposal ID for bookmarking
      if (resultId && !proposalId) {
        const basePath = user?.role === 'sales_agent' ? '/sales/proposals' : '/admin/proposals';
        window.history.replaceState(null, '', `${basePath}/${resultId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save proposal');
    } finally {
      setSending(false);
    }
  };

  const handleEmailProposal = async () => {
    if (!savedProposalId) {
      toast.error('Please save the proposal first');
      return;
    }
    
    const allRecipients = [...emailForm.recipients];
    if (!allRecipients.includes(proposal.client_email)) {
      allRecipients.unshift(proposal.client_email);
    }
    
    if (allRecipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }

    setSending(true);
    try {
      const res = await axios.post(`${API}/proposals/${savedProposalId}/send`, {
        recipients: allRecipients,
        subject: emailForm.subject,
        body: emailForm.body
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProposalStatus('sent');
      setShowEmailDialog(false);
      if (res.data?.email_sent === false) {
        // Backend was honest and told us the email did NOT actually go
        // out — surface that loud and clear instead of pretending we sent.
        toast.warning(res.data.message || 'Saved locally — email service is not configured, so the message was NOT delivered. Please re-send or configure email.', { duration: 8000 });
      } else {
        toast.success(`Proposal emailed to ${allRecipients.length} recipient(s)!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const openEmailDialog = () => {
    const template = EMAIL_TEMPLATES.find(t => t.id === 'initial');
    const clientName = proposal.client_name || 'there';
    setEmailForm({
      recipients: proposal.client_email ? [proposal.client_email] : [],
      newRecipient: '',
      subject: template.subject,
      body: template.body.replace(/{client_name}/g, clientName),
      templateId: 'initial'
    });
    setShowEmailDialog(true);
  };

  const selectEmailTemplate = (templateId) => {
    const template = EMAIL_TEMPLATES.find(t => t.id === templateId);
    if (!template) return;
    const clientName = proposal.client_name || 'there';
    setEmailForm(prev => ({
      ...prev,
      templateId,
      subject: template.subject || prev.subject,
      body: template.body ? template.body.replace(/{client_name}/g, clientName) : prev.body
    }));
  };

  const addEmailRecipient = () => {
    const email = emailForm.newRecipient.trim();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    if (emailForm.recipients.includes(email)) {
      toast.error('Email already added');
      return;
    }
    setEmailForm(prev => ({
      ...prev,
      recipients: [...prev.recipients, email],
      newRecipient: ''
    }));
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/proposal-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const saveAsTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      const templateData = {
        event_type: proposal.event_type,
        selected_items: proposal.selected_items,
        terms_conditions: proposal.terms_conditions,
        header_image: proposal.header_image,
        discount_percent: proposal.discount_percent,
        admin_fee_percent: proposal.admin_fee_percent,
        delivery_fee: proposal.delivery_fee,
        tax_percent: proposal.tax_percent,
        basic_details: proposal.basic_details
      };
      
      await axios.post(`${API}/proposal-templates`, {
        name: newTemplateName.trim(),
        description: newTemplateDesc.trim(),
        template_data: templateData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Template saved!');
      setShowSaveTemplateDialog(false);
      setNewTemplateName('');
      setNewTemplateDesc('');
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const loadTemplate = (template) => {
    const td = template.template_data;
    setProposal(prev => ({
      ...prev,
      event_type: td.event_type || prev.event_type,
      selected_items: td.selected_items || prev.selected_items,
      terms_conditions: td.terms_conditions || prev.terms_conditions,
      header_image: td.header_image || prev.header_image,
      discount_percent: td.discount_percent ?? prev.discount_percent,
      admin_fee_percent: td.admin_fee_percent ?? prev.admin_fee_percent,
      delivery_fee: td.delivery_fee ?? prev.delivery_fee,
      tax_percent: td.tax_percent ?? prev.tax_percent,
      basic_details: td.basic_details || prev.basic_details
    }));
    setShowTemplateDialog(false);
    toast.success(`Template "${template.name}" loaded!`);
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/proposal-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(prev => prev.filter(t => t.template_id !== templateId));
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDownloadPDF = async () => {
    if (proposal.selected_items.length === 0) {
      toast.error('Please add at least one service');
      return;
    }

    try {
      toast.loading('Generating PDF...', { id: 'pdf-download' });
      const response = await axios.post(`${API}/proposals/preview-pdf`, {
        ...proposal,
        event_date: proposal.event_date?.toISOString(),
        valid_until: proposal.valid_until?.toISOString(),
        delivery_date: proposal.delivery_date?.toISOString(),
        pickup_date: proposal.pickup_date?.toISOString(),
        subtotal: calculateSubtotal(),
        discount: calculateDiscount(),
        admin_fee: calculateAdminFee(),
        admin_fee_percent: proposal.admin_fee_percent,
        delivery_fee: calculateDeliveryFee(),
        tax: calculateTax(),
        tax_percent: proposal.tax_percent,
        total: calculateTotal(),
        admin_settings: adminSettings
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eventism_proposal_${proposal.client_name || 'draft'}.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Delay cleanup to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 500);

      toast.success('PDF downloaded!', { id: 'pdf-download' });
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('Failed to generate PDF', { id: 'pdf-download' });
    }
  };

  const handleHeaderImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('Uploading image...');
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const uploadedUrl = response.data.url;
      
      // Add to custom gallery
      setCustomGalleryImages(prev => [...prev, { url: uploadedUrl, name: `Custom ${prev.length + 1}` }]);
      
      // Set as current header
      setProposal(prev => ({
        ...prev,
        header_image: uploadedUrl,
        use_default_header: false
      }));
      
      toast.dismiss();
      toast.success('Header image uploaded and added to gallery!');
    } catch (error) {
      toast.dismiss();
      console.error('Upload error:', error);
      toast.error('Failed to upload image. Please try again.');
    }
  };

  const selectGalleryImage = (imageUrl) => {
    setProposal(prev => ({
      ...prev,
      header_image: imageUrl,
      use_default_header: false
    }));
    setShowImageGallery(false);
    toast.success('Header image selected!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background flex items-center justify-center pt-16">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (user?.role !== 'admin' && user?.role !== 'sales_agent') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-background flex items-center justify-center pt-16">
        <Card className="bg-card border-border p-8 text-center">
          <h2 className="text-xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Admin or Sales Agent access required</p>
        </Card>
      </div>
    );
  }

  // Preview/Print Mode
  if (previewMode) {
    return (
      <div className="min-h-screen bg-white pt-16 pb-12 overflow-x-hidden" data-testid="proposal-preview">
        <div className="max-w-4xl mx-auto">
          {/* Action Bar */}
          <div className="sticky top-16 bg-card text-foreground p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between z-10 print:hidden gap-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setPreviewMode(false)} className="text-foreground hover:bg-white/10">
                <ArrowLeft className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Back to Editor</span>
                <span className="sm:hidden">Editor</span>
              </Button>
              {savedProposalId && (
                <Badge 
                  className={
                    proposalStatus === 'draft' ? 'bg-accent' :
                    proposalStatus === 'sent' ? 'bg-blue-500' :
                    proposalStatus === 'viewed' ? 'bg-amber-500' :
                    proposalStatus === 'pending' ? 'bg-purple-500' :
                    proposalStatus === 'accepted' ? 'bg-emerald-500' :
                    proposalStatus === 'declined' ? 'bg-red-500' :
                    'bg-accent'
                  }
                >
                  {proposalStatus.charAt(0).toUpperCase() + proposalStatus.slice(1)}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="border-white/30 text-foreground hover:bg-white/10">
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">PDF</span>
              </Button>
              {savedProposalId && (
                <Button variant="outline" size="sm" onClick={openEmailDialog} className="border-white/30 text-foreground hover:bg-white/10">
                  <Mail className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Email</span>
                </Button>
              )}
              <Button size="sm" onClick={handleSendProposal} disabled={sending} className="bg-indigo-500 hover:bg-indigo-600">
                <Save className="h-4 w-4 sm:mr-2" />
                {sending ? 'Saving...' : (isEditing ? 'Save' : `Save ${docLabel}`)}
              </Button>
            </div>
          </div>

          {/* Proposal Document */}
          <div className="bg-white shadow-lg print:shadow-none">
            {/* Header Image */}
            <div className="h-48 bg-cover bg-center relative" style={{ backgroundImage: `url(${proposal.header_image})` }}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                <div>
                  <img src="/logo.png" alt="Eventism" className="h-8 mb-2" onError={(e) => e.target.style.display = 'none'} />
                  <h1 className="text-3xl font-bold text-foreground">EVENTISM</h1>
                  <p className="text-foreground/80">Premium Event Services</p>
                </div>
                {/* Proposal Number - Top Right */}
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                  <p className="text-foreground/80 text-xs uppercase tracking-wider">{isInvoice ? 'Invoice' : 'Proposal'}</p>
                  <p className="text-foreground font-bold text-lg">{proposal.invoice_number || proposal.proposal_number || (isInvoice ? 'INV-NEW' : 'EP-NEW')}</p>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-8">
              {/* Proposal Title - Event Type on Date format */}
              <div className="text-center mb-6 sm:mb-8">
                <Badge className={`${isInvoice ? 'bg-emerald-600' : 'bg-indigo-500'} mb-3`}>{isInvoice ? 'INVOICE' : 'EVENT PROPOSAL'}</Badge>
                <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 mb-2">
                  {proposal.event_name || proposal.title || proposal.client_name || (isInvoice ? 'Invoice' : 'Event Proposal')}
                </h2>
                {/* Event Type on Date format */}
                {(proposal.event_type || proposal.event_date) && (
                  <p className="text-xl text-muted-foreground">
                    {proposal.event_type || 'Event'} on {proposal.event_date ? format(proposal.event_date, 'MMMM d, yyyy') : 'Date TBD'}
                  </p>
                )}
              </div>

              {/* Client & Event Info Grid */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Client Details */}
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Client Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-slate-800">{proposal.client_name}</p>
                        {proposal.company_name && <p className="text-muted-foreground">{proposal.company_name}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-slate-800">{proposal.client_email}</span>
                    </div>
                    {proposal.client_phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-slate-800">{proposal.client_phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Event Details - With Icons */}
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5" />
                    Event Details
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3">
                      <Sparkles className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground w-20">Type:</span>
                      <span className="font-medium text-slate-800">{proposal.event_type || 'Event'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground w-20">Date:</span>
                      <span className="font-medium text-slate-800">{proposal.event_date ? format(proposal.event_date, 'MMMM d, yyyy') : 'TBD'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground w-20">Time:</span>
                      <span className="font-medium text-slate-800">{formatTimeToStandard(proposal.event_time)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground w-20">Guests:</span>
                      <span className="font-medium text-slate-800">{proposal.guest_count}</span>
                    </div>
                    {proposal.venue && (
                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground w-20">Venue:</span>
                        <span className="font-medium text-slate-800">{proposal.venue}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery/Pickup Info - Only show if filled */}
              {(proposal.delivery_date || proposal.pickup_date) && (
                <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="font-semibold text-amber-700 mb-4 flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    Equipment Delivery & Pickup
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    {proposal.delivery_date && (
                      <div>
                        <p className="text-amber-600 font-medium mb-1">Delivery</p>
                        <p className="text-muted-foreground">
                          {format(proposal.delivery_date, 'MMMM d, yyyy')}
                          {proposal.delivery_time && ` at ${formatTimeToStandard(proposal.delivery_time)}`}
                        </p>
                      </div>
                    )}
                    {proposal.pickup_date && (
                      <div>
                        <p className="text-amber-600 font-medium mb-1">Pickup</p>
                        <p className="text-muted-foreground">
                          {format(proposal.pickup_date, 'MMMM d, yyyy')}
                          {proposal.pickup_time && ` at ${formatTimeToStandard(proposal.pickup_time)}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Note From Eventism - Renamed from Overview */}
              {proposal.basic_details && (
                <div className="mb-8">
                  <h3 className="font-semibold text-indigo-600 mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Note From Eventism
                  </h3>
                  <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-r-lg">
                    <p className="text-muted-foreground whitespace-pre-line">{proposal.basic_details}</p>
                  </div>
                </div>
              )}

              {/* Services Overview - Detailed service descriptions */}
              <div className="mb-8">
                <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Service Overview
                </h3>
                
                <div className="space-y-6">
                  {proposal.selected_items.map((item, idx) => (
                    <div key={item.item_id || idx} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm cursor-pointer hover:border-indigo-400 hover:shadow-md transition-all" onClick={() => setPreviewMode(false)} title="Click to edit this service">
                      <div className="flex flex-col sm:flex-row">
                        {/* Service Image */}
                        {item.image_url && (
                          <div className="w-full sm:w-40 h-40 flex-shrink-0">
                            <img 
                              src={item.image_url} 
                              alt={item.name} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        
                        {/* Service Details */}
                        <div className="flex-1 p-5">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="text-lg font-bold text-slate-900">{item.name}</h4>
                              <span className="text-xs uppercase tracking-wide text-indigo-500 font-medium">{item.category}</span>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-muted-foreground">
                                ${parseFloat(item.price) || 0}/{item.unit || 'person'}
                              </p>
                            </div>
                          </div>
                          
                          {/* What to Expect - Always show section */}
                          <div className="mb-3">
                            <p className="text-sm font-medium text-muted-foreground mb-1">What to Expect:</p>
                            <p className="text-sm text-muted-foreground">
                              {item.what_to_expect || 'Our professional team will deliver exceptional service for your event, handling all setup, service, and cleanup.'}
                            </p>
                          </div>
                          
                          {/* What's Included - Always show section */}
                          <div>
                            <p className="text-sm font-medium text-muted-foreground mb-1">What's Included:</p>
                            <div className="flex flex-wrap gap-2">
                              {(item.whats_included && item.whats_included.length > 0) ? (
                                item.whats_included.map((inc, i) => (
                                  <span 
                                    key={i} 
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full"
                                  >
                                    <CheckCircle className="h-3 w-3" />
                                    {inc}
                                  </span>
                                ))
                              ) : (
                                <>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                                    <CheckCircle className="h-3 w-3" />
                                    Professional Setup
                                  </span>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                                    <CheckCircle className="h-3 w-3" />
                                    Service Staff
                                  </span>
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                                    <CheckCircle className="h-3 w-3" />
                                    Cleanup
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service Financial Detail - Line item pricing */}
              <div className="mb-8">
                <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Service Financial Detail
                </h3>
                
                <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Service</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Rate</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Qty</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposal.selected_items.map((item, idx) => (
                        <tr key={item.item_id || idx} className="border-t border-slate-100">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {item.image_url && (
                                <img 
                                  src={item.image_url} 
                                  alt={item.name} 
                                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                />
                              )}
                              <div>
                                <p className="font-medium text-slate-900">{item.name}</p>
                                <p className="text-xs text-muted-foreground">{item.category}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-muted-foreground">
                            ${parseFloat(item.price) || 0}/{item.unit || 'person'}
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-muted-foreground">
                            {item.quantity}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-slate-900">
                            ${((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1)).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pricing Summary */}
              <div className="bg-slate-50 rounded-lg p-6 mb-8">
                <h3 className="font-semibold text-indigo-600 mb-4 flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Pricing Summary
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium text-slate-800">${calculateSubtotal().toFixed(2)}</span>
                  </div>
                  {proposal.discount_percent > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount ({proposal.discount_percent}%)</span>
                      <span>-${calculateDiscount().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Admin Service Fee ({proposal.admin_fee_percent}%)</span>
                    <span className="font-medium text-slate-800">${calculateAdminFee().toFixed(2)}</span>
                  </div>
                  {proposal.delivery_fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Delivery Fee</span>
                      <span className="font-medium text-slate-800">${calculateDeliveryFee().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax ({proposal.tax_percent}%)</span>
                    <span className="font-medium text-slate-800">${calculateTax().toFixed(2)}</span>
                  </div>
                  <Separator className="my-3" />
                  <div className="flex justify-between text-xl font-bold">
                    <span className="text-slate-900">Total</span>
                    <span className="text-indigo-600">${calculateTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              {proposal.terms_conditions && (
                <div className="mb-8">
                  <h3 className="font-semibold text-indigo-600 mb-3">Terms & Conditions</h3>
                  <div className="bg-slate-50 rounded-lg p-4 text-sm text-muted-foreground whitespace-pre-line">
                    {proposal.terms_conditions}
                  </div>
                </div>
              )}

              {/* Notes */}
              {proposal.notes && (
                <div className="mb-8">
                  <h3 className="font-semibold text-indigo-600 mb-3">Additional Notes</h3>
                  <p className="text-muted-foreground">{proposal.notes}</p>
                </div>
              )}

              {/* Footer */}
              <div className="border-t border-slate-200 pt-6 mt-8 text-center text-sm text-muted-foreground">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-4">
                  <span className="flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    {adminSettings.company_phone}
                  </span>
                  <span className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    {adminSettings.company_email}
                  </span>
                </div>
                <p>{adminSettings.company_address}</p>
                <p className="mt-2 text-indigo-500 font-medium">Thank you for considering Eventism for your event!</p>
                {proposal.valid_until && (
                  <p className="mt-2 text-amber-600">This proposal is valid until {format(proposal.valid_until, 'MMMM d, yyyy')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Edit Mode
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background pt-20 pb-24 lg:pb-12 overflow-x-hidden" data-testid="proposal-generator">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(user?.role === 'sales_agent' ? '/sales' : '/admin/pipeline')} size="sm">
              <ArrowLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-lg sm:text-2xl font-bold text-foreground">
                  {isEditing ? `Edit ${docLabel}` : `${docLabel} Generator`}
                </h1>
                {/* Document Type Toggle */}
                <div className="flex items-center bg-muted/50 rounded-lg p-0.5" data-testid="doc-type-toggle">
                  <button
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${!isInvoice ? 'bg-indigo-600 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setProposal(p => ({ ...p, document_type: 'proposal' }))}
                    data-testid="toggle-proposal"
                  >Proposal</button>
                  <button
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${isInvoice ? 'bg-emerald-600 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setProposal(p => ({ ...p, document_type: 'invoice' }))}
                    data-testid="toggle-invoice"
                  >Invoice</button>
                </div>
                {/* Status Badge */}
                {savedProposalId && (
                  <Badge 
                    data-testid="proposal-status-badge"
                    className={
                      proposalStatus === 'draft' ? 'bg-slate-500' :
                      proposalStatus === 'sent' ? 'bg-blue-500' :
                      proposalStatus === 'viewed' ? 'bg-amber-500' :
                      proposalStatus === 'pending' ? 'bg-purple-500' :
                      proposalStatus === 'accepted' ? 'bg-emerald-500' :
                      proposalStatus === 'declined' ? 'bg-red-500' :
                      'bg-slate-500'
                    }
                  >
                    {proposalStatus.charAt(0).toUpperCase() + proposalStatus.slice(1)}
                  </Badge>
                )}
                {/* Invoice Payment Status */}
                {savedProposalId && isInvoice && proposal.payment_status && (
                  <Badge
                    data-testid="invoice-payment-badge"
                    className={
                      proposal.payment_status === 'paid' ? 'bg-emerald-500' :
                      proposal.payment_status === 'partial_payment' ? 'bg-amber-500' :
                      proposal.payment_status === 'overdue' ? 'bg-red-500' :
                      'bg-slate-400'
                    }
                  >
                    {proposal.payment_status === 'partial_payment' ? 'Partial Payment' : proposal.payment_status.charAt(0).toUpperCase() + proposal.payment_status.slice(1)}
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground text-sm">
                {isEditing
                  ? `${docLabel} ${proposal.invoice_number || proposal.proposal_number || ''}`
                  : isInvoice
                    ? 'Create and send invoices for confirmed orders'
                    : 'Create professional, branded proposals for your clients'
                }
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchTemplates(); setShowTemplateDialog(true); }} data-testid="load-template">
              <FileText className="h-4 w-4 mr-2" />
              Templates
            </Button>
            <Button variant="outline" onClick={() => setPreviewMode(true)} data-testid="toggle-preview">
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} data-testid="download-pdf">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            {savedProposalId && (
              <Button 
                variant="outline"
                onClick={openEmailDialog}
                data-testid="email-proposal-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email
              </Button>
            )}
            <Button 
              onClick={handleSendProposal} 
              disabled={sending}
              className="bg-indigo-500 hover:bg-indigo-600"
              data-testid="send-proposal"
            >
              <Save className="h-4 w-4 mr-2" />
              {sending ? 'Saving...' : (isEditing ? `Save ${docLabel}` : `Create ${docLabel}`)}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Image */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-indigo-500" />
                  Proposal Header
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-32 rounded-lg bg-cover bg-center relative overflow-hidden" style={{ backgroundImage: `url(${proposal.header_image})` }}>
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center gap-3">
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={handleHeaderImageUpload} />
                      <Button variant="secondary" size="sm" className="gap-2" asChild>
                        <span><Upload className="h-4 w-4" /> Upload</span>
                      </Button>
                    </label>
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      className="gap-2"
                      onClick={() => setShowImageGallery(true)}
                    >
                      <ImageIcon className="h-4 w-4" /> Gallery
                    </Button>
                  </div>
                </div>
                
                {/* Image Gallery Preview */}
                <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                  {[...HEADER_IMAGE_GALLERY, ...customGalleryImages].slice(0, 6).map((img, idx) => (
                    <div 
                      key={idx}
                      className={`w-16 h-10 rounded cursor-pointer flex-shrink-0 bg-cover bg-center border-2 transition-all ${
                        proposal.header_image === img.url ? 'border-indigo-500 ring-2 ring-indigo-300' : 'border-transparent hover:border-slate-300'
                      }`}
                      style={{ backgroundImage: `url(${img.url})` }}
                      onClick={() => selectGalleryImage(img.url)}
                      title={img.name}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Client Details */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-500" />
                  Client Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input
                    value={proposal.client_name}
                    onChange={(e) => setProposal({...proposal, client_name: e.target.value})}
                    placeholder="John Doe"
                    data-testid="client-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={proposal.company_name}
                    onChange={(e) => setProposal({...proposal, company_name: e.target.value})}
                    placeholder="Acme Corp"
                    data-testid="company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Email *</Label>
                  <Input
                    type="email"
                    value={proposal.client_email}
                    onChange={(e) => setProposal({...proposal, client_email: e.target.value})}
                    placeholder="john@example.com"
                    data-testid="client-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    value={proposal.client_phone}
                    onChange={(e) => setProposal({...proposal, client_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    data-testid="client-phone"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Event Details */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-indigo-500" />
                  Event Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Name</Label>
                  <Input
                    value={proposal.event_name}
                    onChange={(e) => setProposal({...proposal, event_name: e.target.value})}
                    placeholder="Annual Holiday Party"
                    data-testid="event-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <Select 
                    value={proposal.event_type} 
                    onValueChange={(v) => setProposal({...proposal, event_type: v})}
                  >
                    <SelectTrigger data-testid="event-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Event Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="event-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {proposal.event_date ? format(proposal.event_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={proposal.event_date}
                        onSelect={(date) => setProposal({...proposal, event_date: date})}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Event Time</Label>
                  <Input
                    type="time"
                    value={proposal.event_time}
                    onChange={(e) => setProposal({...proposal, event_time: e.target.value})}
                    data-testid="event-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Guest Count</Label>
                  <Input
                    type="number"
                    value={proposal.guest_count}
                    onChange={(e) => setProposal({...proposal, guest_count: parseInt(e.target.value) || 1})}
                    data-testid="guest-count"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Valid Until</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {proposal.valid_until ? format(proposal.valid_until, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={proposal.valid_until}
                        onSelect={(date) => setProposal({...proposal, valid_until: date})}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Invoice-specific fields */}
                {isInvoice && (
                  <>
                    <div className="space-y-2">
                      <Label>Payment Terms</Label>
                      <select
                        value={proposal.payment_terms || 'due_on_receipt'}
                        onChange={e => setProposal({...proposal, payment_terms: e.target.value})}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="payment-terms"
                      >
                        <option value="due_on_receipt">Due on Receipt</option>
                        <option value="net_15">Net 15</option>
                        <option value="net_30">Net 30</option>
                        <option value="net_60">Net 60</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {proposal.due_date ? format(proposal.due_date, 'PPP') : 'Select due date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={proposal.due_date}
                            onSelect={(date) => setProposal({...proposal, due_date: date})}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </>
                )}

                <div className="sm:col-span-2 space-y-2">
                  <Label>Venue</Label>
                  <Input
                    value={proposal.venue}
                    onChange={(e) => setProposal({...proposal, venue: e.target.value})}
                    placeholder="Event venue name and address"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Equipment Delivery & Pickup (Optional) */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Truck className="h-5 w-5 text-amber-500" />
                  Equipment Delivery & Pickup
                  <span className="text-xs text-muted-foreground font-normal">(Optional - for rentals)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Delivery Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {proposal.delivery_date ? format(proposal.delivery_date, 'PPP') : 'Select'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={proposal.delivery_date}
                        onSelect={(date) => setProposal({...proposal, delivery_date: date})}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Delivery Time</Label>
                  <Input
                    type="time"
                    value={proposal.delivery_time}
                    onChange={(e) => setProposal({...proposal, delivery_time: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pickup Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {proposal.pickup_date ? format(proposal.pickup_date, 'PPP') : 'Select'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={proposal.pickup_date}
                        onSelect={(date) => setProposal({...proposal, pickup_date: date})}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Pickup Time</Label>
                  <Input
                    type="time"
                    value={proposal.pickup_time}
                    onChange={(e) => setProposal({...proposal, pickup_time: e.target.value})}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Note From Eventism */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-indigo-500" />
                  Note From Eventism
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={proposal.basic_details}
                  onChange={(e) => setProposal({...proposal, basic_details: e.target.value})}
                  placeholder="Write an introduction or overview for this proposal. This appears before the service details..."
                  rows={4}
                  data-testid="basic-details"
                />
              </CardContent>
            </Card>

            {/* Pricing Controls */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-indigo-500" />
                  Pricing & Fees
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Percent className="h-3 w-3" /> Discount %
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={proposal.discount_percent}
                    onChange={(e) => setProposal({...proposal, discount_percent: parseFloat(e.target.value) || 0})}
                    data-testid="discount-percent"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Percent className="h-3 w-3" /> Admin Service Fee %
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={proposal.admin_fee_percent}
                    onChange={(e) => setProposal({...proposal, admin_fee_percent: parseFloat(e.target.value) || 0})}
                    data-testid="admin-fee-percent"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Truck className="h-3 w-3" /> Delivery Fee $
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={proposal.delivery_fee}
                    onChange={(e) => setProposal({...proposal, delivery_fee: parseFloat(e.target.value) || 0})}
                    data-testid="delivery-fee"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Percent className="h-3 w-3" /> Tax %
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={proposal.tax_percent}
                    onChange={(e) => setProposal({...proposal, tax_percent: parseFloat(e.target.value) || 0})}
                    data-testid="tax-percent"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Terms & Conditions */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg">Terms & Conditions</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={proposal.terms_conditions}
                  onChange={(e) => setProposal({...proposal, terms_conditions: e.target.value})}
                  placeholder="Enter terms and conditions..."
                  rows={6}
                  className="font-mono text-sm"
                  data-testid="terms-conditions"
                />
              </CardContent>
            </Card>

            {/* Additional Notes */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg">Additional Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={proposal.notes}
                  onChange={(e) => setProposal({...proposal, notes: e.target.value})}
                  placeholder="Any additional notes for the client..."
                  rows={3}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Cart / Services */}
          <div className="space-y-6">
            {/* Add Services Button */}
            <Button 
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              onClick={() => setShowServiceDialog(true)}
              data-testid="add-service-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Service
            </Button>

            {/* Selected Services Cart */}
            <Card className="bg-card border-border sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center justify-between">
                  <span>Services ({proposal.selected_items.length})</span>
                  <Badge variant="outline">{proposal.guest_count} guests</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {proposal.selected_items.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Add services to build your proposal</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {proposal.selected_items.map(item => (
                      <div key={item.item_id} className="bg-muted/30 rounded-lg p-3">
                        <div className="flex items-start gap-3">
                          {item.image_url && (
                            <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground text-sm truncate">{item.name}</p>
                            <p className="text-xs text-muted-foreground">${parseFloat(item.price) || 0}/{item.pricing_model === 'per_person' ? 'person' : item.pricing_model === 'per_hour' ? 'hour' : item.pricing_model === 'per_hour_per_person' ? 'person/hr' : 'flat'}{(item.options_price || 0) > 0 ? ` + $${item.options_price} options` : ''}</p>
                            {/* Show selected options summary */}
                            {item.selected_options && Object.keys(item.selected_options).length > 0 && item.option_groups && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {item.option_groups.map(og => {
                                  const sel = item.selected_options[og.option_id];
                                  if (!sel) return null;
                                  const choices = Array.isArray(sel) ? sel : [sel];
                                  return choices.map(cid => {
                                    const choice = (og.choices || []).find(c => c.choice_id === cid);
                                    return choice ? <Badge key={cid} variant="outline" className="text-[9px] px-1.5 py-0">{choice.name}</Badge> : null;
                                  });
                                })}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateQuantity(item.item_id, -1)}>
                                <Minus className="h-3 w-3" />
                              </Button>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  setProposal(prev => ({
                                    ...prev,
                                    selected_items: prev.selected_items.map(i =>
                                      i.item_id === item.item_id ? { ...i, quantity: Math.max(1, val) } : i
                                    )
                                  }));
                                }}
                                className="w-16 text-center text-sm font-medium h-7 px-1"
                                min={1}
                                data-testid={`quantity-input-${item.item_id}`}
                              />
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => updateQuantity(item.item_id, 1)}>
                                <Plus className="h-3 w-3" />
                              </Button>
                              {item.option_groups && item.option_groups.length > 0 && (
                                <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 ml-1" onClick={() => openCustomization(item)} data-testid={`customize-${item.item_id}`}>
                                  Customize
                                </Button>
                              )}
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive ml-auto" onClick={() => removeItem(item.item_id)}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                            <Input
                              value={item.service_details || ''}
                              onChange={(e) => updateItemDetails(item.item_id, e.target.value)}
                              placeholder="Add service details..."
                              className="mt-2 text-xs h-7"
                            />
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-sm">${((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1) + (item.options_price || 0) * (parseInt(item.quantity) || 1)).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    ))}

                    <Separator />

                    {/* Totals */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>${calculateSubtotal().toFixed(2)}</span>
                      </div>
                      {proposal.discount_percent > 0 && (
                        <div className="flex justify-between text-green-500">
                          <span>Discount ({proposal.discount_percent}%)</span>
                          <span>-${calculateDiscount().toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Admin Service Fee ({proposal.admin_fee_percent}%)</span>
                        <span>${calculateAdminFee().toFixed(2)}</span>
                      </div>
                      {proposal.delivery_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Delivery</span>
                          <span>${calculateDeliveryFee().toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax ({proposal.tax_percent}%)</span>
                        <span>${calculateTax().toFixed(2)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span className="text-indigo-500">${calculateTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Activity & Notes Panel - below editor for saved proposals */}
      {!previewMode && proposal.proposal_id && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 print:hidden">
          <ProposalActivityPanel proposalId={proposal.proposal_id} token={token} />
        </div>
      )}

      {/* Mobile Floating Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-3 flex items-center justify-between z-20 lg:hidden print:hidden" data-testid="mobile-action-bar">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">{proposal.selected_items.length} service(s)</p>
          <p className="text-base font-bold text-indigo-500">${calculateTotal().toFixed(2)}</p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowServiceDialog(true)}
            data-testid="mobile-add-service"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
          <Button 
            size="sm"
            onClick={handleSendProposal} 
            disabled={sending}
            className="bg-indigo-500 hover:bg-indigo-600"
            data-testid="mobile-save-proposal"
          >
            <Save className="h-4 w-4 mr-1" />
            {sending ? '...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Add Service Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="max-h-[80vh] overflow-y-auto" style={{ maxWidth: '90vw', width: 900, resize: 'horizontal', overflow: 'auto', minWidth: 400 }}>
          <DialogHeader>
            <DialogTitle>Add Services</DialogTitle>
          </DialogHeader>
          
          {/* City Filter */}
          <div className="flex gap-2 flex-wrap mb-2">
            <span className="text-xs text-muted-foreground self-center mr-1">Market:</span>
            {['all', 'denver', 'charlotte', 'los_angeles', 'las_vegas'].map(city => {
              const labels = { all: 'All Markets', denver: 'Denver, CO', charlotte: 'Charlotte, NC', los_angeles: 'Los Angeles, CA', las_vegas: 'Las Vegas, NV' };
              return (
                <Button key={city} variant={selectedCity === city ? 'default' : 'outline'} size="sm" onClick={() => setSelectedCity(city)} className="text-xs h-7" data-testid={`city-filter-${city}`}>
                  {labels[city]}
                </Button>
              );
            })}
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 flex-wrap mb-4">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
            >
              All
            </Button>
            {(() => {
              const cats = new Set();
              if (selectedCity === 'all') {
                Object.keys(catalog).forEach(c => cats.add(c));
                cityServices.forEach(s => cats.add(s.category));
              } else {
                cityServices.filter(s => s.city === selectedCity).forEach(s => cats.add(s.category));
                // Also include generic catalog if no city filter
              }
              return [...cats].sort().map(cat => (
                <Button key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} size="sm" onClick={() => setSelectedCategory(cat)} className="gap-1">
                  {CATEGORY_ICONS[cat]} {cat}
                </Button>
              ));
            })()}
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(() => {
              let items = [];
              const cityLabels = { denver: 'Denver', charlotte: 'Charlotte', los_angeles: 'Los Angeles', las_vegas: 'Las Vegas' };

              // City-specific services from service_products
              const filteredCityServices = selectedCity === 'all'
                ? cityServices
                : cityServices.filter(s => s.city === selectedCity);

              filteredCityServices
                .filter(s => selectedCategory === 'all' || s.category === selectedCategory)
                .forEach(s => {
                  items.push({
                    key: s.service_id || s.product_id,
                    item_id: s.service_id || s.product_id,
                    name: s.name,
                    description: s.short_description || s.description,
                    price: s.base_price,
                    image_url: s.image_url,
                    category: s.category,
                    city: s.city,
                    cityLabel: cityLabels[s.city] || s.city,
                    pricing_model: s.pricing_model,
                    option_groups: s.option_groups,
                    what_to_expect: s.what_to_expect,
                    whats_included: s.whats_included,
                    source: 'city',
                  });
                });

              // Generic catalog items (only when 'all' markets selected)
              if (selectedCity === 'all') {
                Object.entries(catalog)
                  .filter(([cat]) => selectedCategory === 'all' || cat === selectedCategory)
                  .flatMap(([cat, catItems]) =>
                    catItems.map(item => ({
                      key: item.item_id,
                      item_id: item.item_id,
                      name: item.name,
                      description: item.description,
                      price: item.price,
                      image_url: item.image_url,
                      category: cat,
                      city: null,
                      cityLabel: 'Generic',
                      pricing_model: null,
                      option_groups: item.option_groups,
                      what_to_expect: item.what_to_expect,
                      whats_included: item.whats_included,
                      source: 'catalog',
                    }))
                  ).forEach(i => items.push(i));
              }

              // Dedupe by name
              const seen = new Set();
              items = items.filter(i => {
                if (seen.has(i.name)) return false;
                seen.add(i.name);
                return true;
              });

              return items.map(item => (
                <div
                  key={item.key}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-indigo-500 hover:bg-indigo-500/5 transition-colors cursor-pointer"
                  onClick={() => addItem({ ...item, item_id: item.item_id })}
                >
                  {item.image_url && (
                    <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-lg object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm font-semibold text-indigo-500">
                        ${parseFloat(item.price) || 0}{item.pricing_model === 'per_person' ? '/person' : item.pricing_model === 'per_hour' ? '/hr' : item.pricing_model === 'flat_rate' ? ' flat' : '/person'}
                      </p>
                      {item.cityLabel && item.cityLabel !== 'Generic' && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0">{item.cityLabel}</Badge>
                      )}
                      {item.option_groups?.length > 0 && <Badge variant="outline" className="text-[9px] px-1.5 py-0">Customizable</Badge>}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" className="flex-shrink-0">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              ));
            })()}
          </div>
          
          {/* Add Custom Service Button */}
          <div className="border-t pt-4 mt-4">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => {
                setShowServiceDialog(false);
                setShowCustomServiceDialog(true);
              }}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Custom Service
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customization Dialog — matches Plan Your Event ServiceDetailModal */}
      <Dialog open={!!customizingItem} onOpenChange={(open) => { if (!open) { setCustomizingItem(null); setCustomizationOptions({}); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          {customizingItem && (() => {
            const svc = customizingItem;
            const isPP = svc.pricing_model === 'per_person' || svc.pricing_model === 'per_hour_per_person';
            const isPH = svc.pricing_model === 'per_hour' || svc.pricing_model === 'per_hour_per_person';

            // Calculate total with options
            let optsMod = 0;
            (svc.option_groups || []).forEach(og => {
              const sel = customizationOptions[og.option_id];
              (Array.isArray(sel) ? sel : sel ? [sel] : []).forEach(cid => {
                const c = (og.choices || []).find(ch => ch.choice_id === cid);
                if (c) optsMod += parseFloat(c.price_modifier) || 0;
              });
            });
            const unitPrice = (parseFloat(svc.price) || 0) + optsMod;

            // Get local guest/hour state from the customizing item or proposal defaults
            const existingInCart = proposal.selected_items.find(i => i.item_id === svc.item_id);
            const defaultGuests = existingInCart?.quantity || (isPP ? proposal.guest_count : 1);
            const defaultHours = existingInCart?.hours || 4;

            return (
              <>
                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-2">
                  <DialogHeader>
                    <DialogTitle className="text-xl">{svc.name}</DialogTitle>
                    <div className="flex gap-1.5 flex-wrap mt-1">
                      <Badge variant="secondary" className="text-xs capitalize">{svc.category}</Badge>
                      {svc.pricing_model && <Badge variant="outline" className="text-xs">{svc.pricing_model === 'per_person' ? 'Per Person' : svc.pricing_model === 'per_hour' ? 'Per Hour' : svc.pricing_model === 'flat_rate' ? 'Flat Rate' : svc.pricing_model}</Badge>}
                    </div>
                  </DialogHeader>

                  {/* Image */}
                  {svc.image_url && (
                    <div className="mt-4 rounded-lg overflow-hidden aspect-video bg-muted">
                      <img src={svc.image_url} alt={svc.name} className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Description */}
                  <p className="text-sm text-muted-foreground mt-4">{svc.description || svc.short_description}</p>

                  {/* What's Included */}
                  {svc.whats_included && svc.whats_included.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold mb-2">What's Included</h4>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {svc.whats_included.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                            <Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Option Groups */}
                  {(svc.option_groups || []).length > 0 && (
                    <div className="mt-6 space-y-5">
                      <h4 className="text-sm font-semibold">Customize Your Order</h4>
                      {svc.option_groups.map(og => (
                        <div key={og.option_id} className="space-y-2" data-testid={`option-group-${og.option_id}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{og.name}</span>
                            {og.required && <Badge className="bg-red-500/20 text-red-400 text-[9px]">Required</Badge>}
                            {og.multiple_select && <Badge variant="outline" className="text-[9px]">Select multiple</Badge>}
                          </div>
                          {og.description && <p className="text-xs text-muted-foreground">{og.description}</p>}
                          <div className="space-y-1.5">
                            {(og.choices || []).map(choice => {
                              const isSelected = og.multiple_select
                                ? (customizationOptions[og.option_id] || []).includes(choice.choice_id)
                                : customizationOptions[og.option_id] === choice.choice_id;
                              return (
                                <button
                                  key={choice.choice_id}
                                  onClick={() => toggleOption(og.option_id, choice.choice_id, og.multiple_select)}
                                  className={`w-full text-left flex items-center gap-3 p-3 rounded-lg border transition-all ${
                                    isSelected ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:border-indigo-500/30'
                                  }`}
                                  data-testid={`choice-${choice.choice_id}`}
                                >
                                  <div className={`w-5 h-5 rounded-${og.multiple_select ? 'md' : 'full'} border-2 flex items-center justify-center flex-shrink-0 ${
                                    isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-muted-foreground/40'
                                  }`}>
                                    {isSelected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium">{choice.name}</p>
                                    {choice.description && <p className="text-[10px] text-muted-foreground">{choice.description}</p>}
                                  </div>
                                  {(parseFloat(choice.price_modifier) || 0) !== 0 && (
                                    <span className={`text-xs font-semibold flex-shrink-0 ${parseFloat(choice.price_modifier) > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                                      {parseFloat(choice.price_modifier) > 0 ? '+' : ''}${parseFloat(choice.price_modifier).toFixed(2)}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sticky Footer — matches Plan Your Event */}
                <div className="sticky bottom-0 bg-card border-t border-border p-4 sm:p-5 space-y-3" data-testid="customization-footer">
                  {/* Guest / Hour Controls */}
                  <div className="flex items-center gap-4">
                    {isPP && (
                      <div className="flex items-center gap-2 flex-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Guests:</span>
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => {
                          const newVal = Math.max(svc.min_guests || 1, defaultGuests - 10);
                          if (existingInCart) {
                            setProposal(prev => ({ ...prev, selected_items: prev.selected_items.map(i => i.item_id === svc.item_id ? { ...i, quantity: newVal } : i) }));
                          }
                          setCustomizingItem(prev => ({ ...prev, _localGuests: newVal }));
                        }}><Minus className="h-3 w-3" /></Button>
                        <Input
                          type="number"
                          value={existingInCart?.quantity || customizingItem._localGuests || defaultGuests}
                          onChange={(e) => {
                            const val = Math.max(1, parseInt(e.target.value) || 1);
                            if (existingInCart) {
                              setProposal(prev => ({ ...prev, selected_items: prev.selected_items.map(i => i.item_id === svc.item_id ? { ...i, quantity: val } : i) }));
                            }
                            setCustomizingItem(prev => ({ ...prev, _localGuests: val }));
                          }}
                          className="w-16 text-center text-sm h-7"
                          min={svc.min_guests || 1}
                          max={svc.max_guests || 1000}
                        />
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => {
                          const newVal = Math.min(svc.max_guests || 1000, defaultGuests + 10);
                          if (existingInCart) {
                            setProposal(prev => ({ ...prev, selected_items: prev.selected_items.map(i => i.item_id === svc.item_id ? { ...i, quantity: newVal } : i) }));
                          }
                          setCustomizingItem(prev => ({ ...prev, _localGuests: newVal }));
                        }}><Plus className="h-3 w-3" /></Button>
                      </div>
                    )}
                    {isPH && (
                      <div className="flex items-center gap-2 flex-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Hours:</span>
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => {
                          const newVal = Math.max(svc.min_hours || 1, defaultHours - 1);
                          setCustomizingItem(prev => ({ ...prev, _localHours: newVal }));
                        }}><Minus className="h-3 w-3" /></Button>
                        <span className="text-sm font-medium w-8 text-center">{customizingItem._localHours || defaultHours}</span>
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => {
                          const newVal = Math.min(svc.max_hours || 12, (customizingItem._localHours || defaultHours) + 1);
                          setCustomizingItem(prev => ({ ...prev, _localHours: newVal }));
                        }}><Plus className="h-3 w-3" /></Button>
                      </div>
                    )}
                  </div>

                  {/* Price + Add Button */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-foreground">
                        ${(() => {
                          const guests = existingInCart?.quantity || customizingItem._localGuests || defaultGuests;
                          const hours = customizingItem._localHours || defaultHours;
                          if (isPP && isPH) return (unitPrice * guests * hours).toFixed(2);
                          if (isPP) return (unitPrice * guests).toFixed(2);
                          if (isPH) return (unitPrice * hours).toFixed(2);
                          return unitPrice.toFixed(2);
                        })()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        ${unitPrice.toFixed(2)}{isPP ? `/person` : isPH ? `/hour` : ' flat'}
                        {optsMod > 0 ? ` (incl. $${optsMod.toFixed(2)} options)` : ''}
                      </p>
                    </div>
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700 px-6"
                      onClick={() => {
                        const guests = existingInCart?.quantity || customizingItem._localGuests || defaultGuests;
                        addItemDirect({
                          ...svc,
                          _localGuests: guests,
                          _localHours: customizingItem._localHours || defaultHours,
                        }, customizationOptions);
                      }}
                      data-testid="confirm-customization"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      {existingInCart ? 'Update' : 'Add to Proposal'}
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Custom Service Dialog */}
      <Dialog open={showCustomServiceDialog} onOpenChange={setShowCustomServiceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Custom Service</DialogTitle>
            <DialogDescription>
              Create a custom service for items not in our catalog
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="custom-name">Service Name *</Label>
              <Input
                id="custom-name"
                placeholder="e.g., Custom Ice Sculpture"
                value={customService.name}
                onChange={(e) => setCustomService(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="custom-description">Description</Label>
              <Textarea
                id="custom-description"
                placeholder="Describe the service..."
                value={customService.description}
                onChange={(e) => setCustomService(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="custom-price">Price *</Label>
                <Input
                  id="custom-price"
                  type="number"
                  placeholder="0.00"
                  value={customService.price || ''}
                  onChange={(e) => setCustomService(prev => ({ ...prev, price: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="custom-quantity">Quantity</Label>
                <Input
                  id="custom-quantity"
                  type="number"
                  min="1"
                  value={customService.quantity}
                  onChange={(e) => setCustomService(prev => ({ ...prev, quantity: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="custom-category">Category</Label>
                <Select
                  value={customService.category}
                  onValueChange={(value) => setCustomService(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom</SelectItem>
                    <SelectItem value="food">Food</SelectItem>
                    <SelectItem value="drinks">Drinks</SelectItem>
                    <SelectItem value="entertainment">Entertainment</SelectItem>
                    <SelectItem value="decor">Decor</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="custom-unit">Unit Type</Label>
                <Select
                  value={customService.unit}
                  onValueChange={(value) => setCustomService(prev => ({ ...prev, unit: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="service">Per Service</SelectItem>
                    <SelectItem value="person">Per Person</SelectItem>
                    <SelectItem value="hour">Per Hour</SelectItem>
                    <SelectItem value="day">Per Day</SelectItem>
                    <SelectItem value="unit">Per Unit</SelectItem>
                    <SelectItem value="flat">Flat Rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="custom-expect">What to Expect</Label>
              <Textarea
                id="custom-expect"
                placeholder="Describe what the client can expect..."
                value={customService.what_to_expect}
                onChange={(e) => setCustomService(prev => ({ ...prev, what_to_expect: e.target.value }))}
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="custom-included">What's Included (comma-separated)</Label>
              <Input
                id="custom-included"
                placeholder="e.g., Setup, Delivery, Cleanup"
                value={customService.whats_included}
                onChange={(e) => setCustomService(prev => ({ ...prev, whats_included: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomServiceDialog(false)}>
              Cancel
            </Button>
            <Button onClick={addCustomService} className="bg-indigo-500 hover:bg-indigo-600">
              Add Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Gallery Dialog */}
      <Dialog open={showImageGallery} onOpenChange={setShowImageGallery}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Header Image</DialogTitle>
            <DialogDescription>
              Choose from our gallery or upload your own image
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 py-4">
            {[...HEADER_IMAGE_GALLERY, ...customGalleryImages].map((img, idx) => (
              <div 
                key={idx}
                className={`aspect-[4/1] rounded-lg cursor-pointer bg-cover bg-center border-2 transition-all hover:scale-105 ${
                  proposal.header_image === img.url ? 'border-indigo-500 ring-2 ring-indigo-300' : 'border-slate-200 hover:border-indigo-300'
                }`}
                style={{ backgroundImage: `url(${img.url})` }}
                onClick={() => selectGalleryImage(img.url)}
              >
                <div className="w-full h-full flex items-end p-2 bg-gradient-to-t from-black/50 to-transparent rounded-lg">
                  <span className="text-xs text-foreground font-medium">{img.name}</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="border-t pt-4">
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={handleHeaderImageUpload} />
              <Button variant="outline" className="w-full gap-2" asChild>
                <span><Upload className="h-4 w-4" /> Upload New Image to Gallery</span>
              </Button>
            </label>
          </div>
        </DialogContent>
      </Dialog>


      {/* Template Gallery Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-500" />
              Proposal Templates
            </DialogTitle>
            <DialogDescription>
              Load a saved template or save the current proposal as a template
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-2 max-h-80 overflow-y-auto">
            {templates.length === 0 ? (
              <p className="text-center text-muted-foreground py-6">
                No templates saved yet. Create your first template from a proposal!
              </p>
            ) : (
              templates.map(tpl => (
                <div key={tpl.template_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-1 cursor-pointer" onClick={() => loadTemplate(tpl)}>
                    <p className="font-medium text-sm">{tpl.name}</p>
                    {tpl.description && <p className="text-xs text-muted-foreground mt-0.5">{tpl.description}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      {tpl.template_data?.selected_items?.length || 0} services | {tpl.template_data?.event_type || 'Any event'}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteTemplate(tpl.template_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Close
            </Button>
            <Button onClick={() => { setShowTemplateDialog(false); setShowSaveTemplateDialog(true); }} className="bg-indigo-500 hover:bg-indigo-600">
              <Plus className="h-4 w-4 mr-2" />
              Save Current as Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save Proposal Template</DialogTitle>
            <DialogDescription>
              Save the current services, pricing, and settings as a reusable template
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g., Wedding Package, Corporate Event..."
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={newTemplateDesc}
                onChange={(e) => setNewTemplateDesc(e.target.value)}
                placeholder="Brief description of what's included..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveAsTemplate} className="bg-indigo-500 hover:bg-indigo-600" data-testid="save-template-btn">
              <Save className="h-4 w-4 mr-2" />
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Email Proposal Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-xl z-[100]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-500" />
              Email {docLabel}
            </DialogTitle>
            <DialogDescription>
              Send this proposal to your client and additional contacts
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Template Selection */}
            <div className="space-y-2">
              <Label>Message Template</Label>
              <Select value={emailForm.templateId} onValueChange={selectEmailTemplate}>
                <SelectTrigger data-testid="email-template-select">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {EMAIL_TEMPLATES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Recipients */}
            <div className="space-y-2">
              <Label>Recipients</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {emailForm.recipients.map((email, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1 py-1">
                    {email}
                    <button 
                      onClick={() => setEmailForm(prev => ({
                        ...prev,
                        recipients: prev.recipients.filter((_, i) => i !== idx)
                      }))}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={emailForm.newRecipient}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, newRecipient: e.target.value }))}
                  placeholder="Add another email address..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmailRecipient())}
                  data-testid="email-new-recipient"
                />
                <Button variant="outline" onClick={addEmailRecipient} type="button">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject..."
                data-testid="email-subject"
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Write your message..."
                rows={6}
                data-testid="email-body"
              />
              <p className="text-xs text-muted-foreground">
                The proposal link will be automatically included in the email.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEmailProposal} 
              disabled={sending || emailForm.recipients.length === 0}
              className="bg-indigo-500 hover:bg-indigo-600"
              data-testid="send-email-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {sending ? 'Sending...' : `Send to ${emailForm.recipients.length} recipient(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
